/* eslint-disable import/prefer-default-export */
import styled, { css } from 'styled-components';
import { breakpoint, font } from 'styles/globals';
import { getBodyStyle, getColor, Theme } from 'styles/theme';

export const NavigationWrapper = styled.nav<{ $headerHeight: number }>`
  height: 100%;
  position: sticky;
  z-index: 10;
  top: ${({ $headerHeight }) => `${$headerHeight + 4}px`};
  max-width: 100%;

  @media (${breakpoint.lgMin}) {
    position: relative;
  }
`;

export const DesktopWrapper = styled.div<{ $headerHeight: number }>`
  display: none;
  position: sticky;
  max-width: 100%;
  top: ${({ $headerHeight }) => `${$headerHeight + 24}px`};
  @media (${breakpoint.lgMin}) {
    display: block;
  }
`;

export const MobileWrapper = styled.div<{ theme: Theme; $isOpened?: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  height: 56px;
  background-color: ${getColor('surfaceBrand')};
  margin: 0 calc(var(--Grid-Margin) * -1);

  ${({ $isOpened }) =>
    !$isOpened &&
    css<{ theme: Theme }>`
      box-shadow: 0 8px 8px -1px ${getColor('neutralLightGray3')}40;
  `}

  @media (${breakpoint.mmMin}) {
    height: 80px;
  }

  @media (${breakpoint.lgMin}) {
    display: none;
  }
`;

export const ContentWrapper = styled.div<{
  theme: Theme;
  $headerHeight?: number;
  $isDropdown?: boolean;
  $isOpened?: boolean;
}>`
  display: block;
  font-family: ${font.graphikCond};
  font-weight: 600;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: 0.5px;
  padding: 0 24px;
  overflow-y: auto;
  max-height: ${({ $headerHeight }) => `calc(100vh - ${$headerHeight}px - 138px)`};

  ${({ $isDropdown, $isOpened }) =>
    $isDropdown &&
    css<{ theme: Theme }>`
      display: ${$isOpened ? 'block' : 'none'};
      width: 100%;
      background-color: ${getColor('surfaceWhite')};
      border-top: ${getColor('borderSecondary')} 1px solid;
      padding: 24px;
      z-index: 1;
      box-shadow: 0 8px 8px -1px ${getColor('neutralLightGray3')}40;
  `}

  @media (${breakpoint.lgMin}) {
    padding-left: 0;
    padding-right: 12px;
    margin-top: 16px;
    margin-bottom: 16px;
    max-height: ${({ $headerHeight }) => `calc(100vh - ${$headerHeight}px - 101px)`};
  }

  & > ol > li,
  & > ul > li  {
    margin-bottom: 8px;

    & > a {
      width: 100%;
    }

    @media (${breakpoint.mdMin}) {
      margin-bottom: 16px;

      :last-child {
        margin-bottom: 0;
      }
    }
  }

  li > ol {
    list-style: decimal inside;
  }
  
  li > ul {
    list-style: none inside;
  }

  li > ul > li > a {
    width: 100%;
  }

  li > ol > li > a {
    width: calc(100% - 23px);
  }

  li > ul,
  li > ol {
    padding-left: 24px;

    & > li > a {
      vertical-align: text-top;
    }

    & > li {
      margin-bottom: 16px;
      line-height: 20px;

      @media (${breakpoint.lgMin}) {
        &, & > a {
          color: ${getColor('textPrimary')};
        }
      }
    }

    & > li:first-child {
      margin-top: 16px; 
    }
  }

  a {
    color: ${getColor('textPrimary')};
    text-decoration: none;
    display: inline-block;
    position: relative;

    &:not(:only-child) {
      width: 100%;
      padding-right: 18px;
      position: relative;
      
      &:after {
        content: url("/icons/BreadcrumbsBlackSeparator.svg");
        transition: transform 0.3s;
        position: absolute;
        right: 0;
        width: 18px;
        height: 18px;
        margin: 5px 0;
      }

      &:hover::after {
        content: url("/icons/BreadcrumbsSeparatorHover.svg");
      }
    }

    &.closed + ol,
    &.closed + ul {
      display: none; 
    }

    &.open + ol,
    &.open + ul {
      display: block;
      font-weight: 400;
    }

    &.open:after {
      transform: rotate(180deg);
    }
       
    &.active-link {
      color: ${getColor('textAccentPrimary')};

      &.open::after,
      &.closed::after {
        content: url("/icons/BreadcrumbsSeparatorHover.svg");
      }
    }

    &:hover {
      color: ${getColor('textAccentPrimary')};
    }
  }

  li > ul > li a:not(:only-child):after,
  li > ol >li a:not(:only-child):after{
    margin: 1px 0;
  }
`;

export const TableTitle = styled.div<{ theme: Theme }>`
  width: 100%;
  ${getBodyStyle('graphikCompact', { default: 'default' }, 'semiBold')};
  color: ${getColor('textWhite')};
  border-bottom: ${getColor('borderSecondary')} 1px solid;
  padding-bottom: 16px;
  height: 80%;  
  overflow: hidden;
  display: none;
   
  &.dropdown {
    width: fit-content;
    border-bottom: none;
    margin: 0;
    padding: 0 24px;
  }

  @media (${breakpoint.mmMin}) {
    display: flex;
    align-items: center;  
    &.dropdown {
      border-right: ${getColor('borderPrimary')} 1px solid;
    }
  }

  @media (${breakpoint.mdMin}) {
    font-size: 20px;
  }

  @media (${breakpoint.lgMin}) {
    color: ${getColor('textPrimary')};
    max-width: 100%;
  }
`;

export const DescriptionAndArrowWrapper = styled.div<{ $dropdownOpened?: boolean; theme: Theme }>`
  display: flex;
  height: 100%;
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  cursor: pointer;

  & > span {
    display: inline;
    ${getBodyStyle('graphikCompact', { default: 'small' }, 'semiBold')};
    color: ${getColor('textWhite')};
  }

  &:after {
    content: '';
    background: url('/icons/ArrowWhite.svg') no-repeat center/contain;
    width: 18px;
    height: 18px;
  }

  ${({ $dropdownOpened }) =>
    $dropdownOpened &&
    css`
      &:after {
        transform: rotate(180deg);
      }
  `}
`;
